import { Fragment, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useAnalyticsWrapper } from "../../../../../store/store";
import { Child, Container, Header, LayerContent } from "../style";
import { ChartContainer, NoData } from "../../style";
import { barOptions, barColors, handlePointClicked } from "../formUtils";
import SectionModal from "./SectionModal";
import {
  Grid,
  GridColumn,
  GridColumnProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { woundAnalysisDetailCols } from "../../dummyData/colums";

// Section 9 — Resident Transfer & Mobility Status (bar chart) and the
// Mobility x Wound Type cross-tab. Reads woundAnalysisIncidents.mobilityStatus.
// Per QA feedback the standalone breakdown table was removed — the bar + cross-tab carry it.
// Transfer_Mobility__c is multi-select — every selected value is counted.
// Cross-tab quality flag: red = Bed-bound + Pressure Ulcer, amber = Chairfast + Pressure Ulcer.

const PressureUlcerCrossTabCell = (props: GridCustomCellProps) => {
  const { dataItem, field, tdProps } = props;
  const value = dataItem[field || ""] ?? 0;
  let background: string | undefined;
  let color: string | undefined;
  if (value > 0 && dataItem.mobility === "Bed-bound") {
    background = "#fde8ed";
    color = "#c0394f";
  } else if (value > 0 && dataItem.mobility === "Chairfast") {
    background = "#fdefd8";
    color = "#9a5c1a";
  }
  return (
    <td
      {...tdProps}
      style={{
        ...tdProps?.style,
        textAlign: "center",
        background,
        color,
        fontWeight: background ? 500 : undefined,
      }}
    >
      {value}
    </td>
  );
};

const dividerStyle: React.CSSProperties = {
  fontSize: "10px",
  fontWeight: 500,
  color: "#bbb",
  textTransform: "uppercase",
  letterSpacing: ".05em",
  paddingTop: "12px",
  marginTop: "12px",
  borderTop: "1px solid #ede8f5",
};

const MobilityStatus = ({ setClickedIncident }: any) => {
  const [crossTab, setCrossTab] = useState<any[]>([]);
  const [crossGrid, setCrossGrid] = useState<any>([]);
  const [barData, setBarData] = useState<any>(null);
  const [mobility, setMobility] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [mobilityDetails, setMobilityDetails] = useState<any>([]);

  const { startDate, endDate, woundFacility, woundAnalysisIncidents } =
    useAnalyticsWrapper((state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      woundFacility: state.woundFacility,
      woundAnalysisIncidents: state.woundAnalysisIncidents,
    }));

  useEffect(() => {
    const section = woundAnalysisIncidents?.mobilityStatus;
    const mb = section?.mobilityBreakdown;
    const ct = section?.crossTab;

    if (mb?.length) {
      setBarData({
        labels: mb.map((r: any) => r.mobility),
        datasets: [
          {
            label: "Mobility Status",
            backgroundColor: barColors(mb.length),
            borderWidth: 1,
            data: mb.map((r: any) => r.count ?? 0),
          },
        ],
      });
    } else {
      setBarData(null);
    }

    if (ct?.length) {
      const rows = ct.map((r: any) => ({
        mobility: r.mobility,
        pressureUlcer: r.pressureUlcer ?? 0,
        skinTear: r.skinTear ?? 0,
        trauma: r.trauma ?? 0,
        blister: r.blister ?? 0,
        other: r.other ?? 0,
        total: r.total ?? 0,
      }));
      const totals = rows.reduce(
        (acc: any, r: any) => ({
          pressureUlcer: acc.pressureUlcer + r.pressureUlcer,
          skinTear: acc.skinTear + r.skinTear,
          trauma: acc.trauma + r.trauma,
          blister: acc.blister + r.blister,
          other: acc.other + r.other,
          total: acc.total + r.total,
        }),
        { pressureUlcer: 0, skinTear: 0, trauma: 0, blister: 0, other: 0, total: 0 }
      );
      rows.push({ mobility: "Total", ...totals });
      setCrossTab(rows);
    } else {
      setCrossTab([]);
    }
  }, [woundAnalysisIncidents]);

  const title = "Resident Transfer & Mobility Status";

  const handleBarClicked = (chart: any) => {
    if (!chart.length) return;
    const label = handlePointClicked(chart, barData?.labels);
    setMobility(label);
    setMobilityDetails(
      woundAnalysisIncidents?.mobilityStatus?.drillDownByMobility?.[label] || []
    );
    setShowModal(true);
  };

  // Cross-tab grid (Mobility x Wound Type)
  useEffect(() => {
    const cellCountProps = {
      cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
    };
    setCrossGrid(
      <Fragment>
        <Grid data={crossTab || []} scrollable="scrollable" rows={{ data: TotalRow }}>
          <GridColumn field="mobility" title="Mobility" width="110px" />
          <GridColumn
            field="pressureUlcer"
            title="Pressure Ulcer"
            width="120px"
            cells={{ data: PressureUlcerCrossTabCell, headerCell: HeaderCenteredCell }}
          />
          <GridColumn field="skinTear" title="Skin Tear" width="95px" {...(cellCountProps as GridColumnProps)} />
          <GridColumn field="trauma" title="Trauma" width="90px" {...(cellCountProps as GridColumnProps)} />
          <GridColumn field="blister" title="Blister" width="90px" {...(cellCountProps as GridColumnProps)} />
          <GridColumn field="other" title="Other" width="90px" {...(cellCountProps as GridColumnProps)} />
          <GridColumn field="total" title="Total" width="80px" {...(cellCountProps as GridColumnProps)} />
        </Grid>
      </Fragment>
    );
  }, [crossTab]);

  return (
    <LayerContent>
      <Header>{title}</Header>
      <Container>
        <Child flex="60%">
          <div style={dividerStyle}>Mobility × Wound Type cross-tab</div>
          {crossTab.length > 0 ? crossGrid : <NoData>No Data</NoData>}
        </Child>
        <Child flex="40%">
          <ChartContainer>
            {barData ? (
              <Bar
                options={{ ...barOptions(handleBarClicked), aspectRatio: 1.1 }}
                data={barData}
              />
            ) : (
              <NoData>No Data</NoData>
            )}
          </ChartContainer>
        </Child>
      </Container>
      <p
        style={{
          fontSize: "11px",
          color: "#999",
          marginTop: "8px",
          fontStyle: "italic",
        }}
      >
        Counts may exceed the incident total — residents may have multiple mobility
        statuses.
      </p>

      {showModal ? (
        <SectionModal
          open={showModal}
          facilityName={(woundFacility?.label ?? woundFacility?.[0]?.label)}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowModal(false)}
          resetFn={() => {}}
          isLoading={false}
          tableData={mobilityDetails}
          tableCols={woundAnalysisDetailCols()}
          title={`${title} - ${mobility}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: mobilityDetails,
              columnData: woundAnalysisDetailCols(),
              fileName: (woundFacility?.label ?? woundFacility?.[0]?.label),
              startDate,
              endDate,
              subTitle: `${title} - ${mobility}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              mobilityDetails,
              woundAnalysisDetailCols(),
              `${(woundFacility?.label ?? woundFacility?.[0]?.label)} - ${title} - ${mobility}`
            )
          }
        />
      ) : null}
    </LayerContent>
  );
};

export default MobilityStatus;
